import { queryOptions, useQuery } from "@tanstack/react-query"
import { z } from "zod"
import {
  routes,
  type AutomaticValueRecoveryStatus,
  type ProductValueRecoveryChanges,
} from "@nova-ai/shared"
import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

const controlsSchema = z.object({
  controls: z.object({
    replacementEnabled: z.boolean(),
    validationEnabled: z.boolean(),
    revision: z.number().int().nonnegative(),
  }),
})

export function automaticValueRecoveryOptions(marketplace: string, runId?: number) {
  return queryOptions({
    queryKey: queryKeys.automaticValueRecovery(marketplace, runId),
    queryFn: async ({ signal }) => {
      const response = await apiFetch<AutomaticValueRecoveryStatus>(
        routes.productValues.recoveryStatus({ marketplace }),
        { signal, query: runId === undefined ? {} : { originatingRunId: String(runId) } }
      )
      if (!response.ok) {
        throw new Error(response.error.message)
      }
      // Missing or malformed settings must never become saved OFF controls.
      const { controls } = controlsSchema.parse(response.value)
      return { ...response.value, controls }
    },
    staleTime: runId === undefined ? 0 : 5_000,
    retry: runId === undefined ? false : 1,
    refetchOnMount: runId === undefined ? "always" : true,
    refetchOnWindowFocus: runId === undefined,
    refetchInterval: runId === undefined ? false : 5_000,
  })
}

export function useAutomaticValueRecovery(marketplace: string, runId?: number) {
  return useQuery(automaticValueRecoveryOptions(marketplace, runId))
}
export function useRecoveryChanges(
  marketplace: string,
  generationId: number | undefined,
  offset: number,
  fingerprint?: string
) {
  return useQuery({
    queryKey: queryKeys.productValueRecoveryChanges(marketplace, generationId, offset, fingerprint),
    enabled: generationId !== undefined,
    queryFn: async ({ signal }) => {
      const response = await apiFetch<ProductValueRecoveryChanges>(
        routes.productValues.recoveryChanges({ marketplace }),
        {
          signal,
          query: {
            generationId: String(generationId),
            offset: String(offset),
            limit: "25",
            ...(fingerprint ? { fingerprint } : {}),
          },
        }
      )
      if (!response.ok) {
        throw new Error(response.error.message)
      }
      return response.value
    },
    retry: false,
  })
}
